// src/components/games/dungeon/dungeonCommands.ts

import { Character, GameState, Room } from './dungeonTypes';
import { itemDescs, examineObjects, cantTakeObjects } from './dungeonData';
import { findMatch, longDir, removeFromArray, hasScrolls, removeScrolls } from './dungeonUtils';
import { slideTile, isPuzzleSolved, formatPuzzleDisplay } from './dungeonPuzzles';
import { updateRoomDescriptions } from './dungeonState';

export interface CommandHandlers {
  print: (text: string, className?: string) => void;
  showPuzzle: () => void;
  describe: (room: Room, full?: boolean) => void;
  moveToRoom: (roomName: string) => void;
  onGameComplete?: () => void;
}

// Movement handler
export function handleMove(
  direction: string,
  char: Character,
  gs: GameState,
  dungeon: { [key: string]: Room },
  room: Room,
  handlers: CommandHandlers
): void {
  const d = longDir(direction);
  const { print, moveToRoom } = handlers;

  if (d === "east" && room.sd === "closet") {
    print("Moving further back in the dark closet, you find a small button hidden in the mist. Pushing reveals a secret hatch with stairs going down, to the source of the fog. You descend.");
    moveToRoom("secret");
  } else if (d === "down" && room.sd === "closet" && dungeon["secret"].v) {
    print("You descend through the secret hatch that you discovered earlier.");
    moveToRoom("secret");
  } else if (d === "west" && room.sd === "attic room") {
    print("You take a short path to the west through a roughly carved stone tunnel to discover an expansive library.");
    moveToRoom("library");
  } else if (d === "east" && room.sd === "library") {
    print("You return through the carved stone tunnel back to the cramped attic.");
    moveToRoom("attic room");
  } else if (d === "down" && room.sd === "library") {
    print("You push aside a heavy bookshelf revealing a narrow stairway descending into darkness.");
    moveToRoom("basement");
  } else if (d === "up" && room.sd === "attic room" && !room.c.includes("folding ladder")) {
    print("You try to climb up to the opening above, but it's too high without something to stand on.");
  } else if (d === "up" && room.sd === "attic room" && room.c.includes("folding ladder")) {
    print("You unfold the wooden ladder and climb up to discover an observatory above.");
    moveToRoom("observatory");
  } else if (d === "down" && room.sd === "center room" && !gs.trap) {
    print("You see nothing but solid stone floor.");
  } else if (d === "down" && room.sd === "center room" && gs.trap) {
    print("You descend through the revealed trapdoor into the depths below.");
    moveToRoom("trapdoor hold");
  } else if (d === "west" && room.sd === "basement" && !gs.mech) {
    print("A heavy stone door blocks your path. Ancient mechanisms must be activated to open it.");
  } else if (d === "west" && room.sd === "basement" && gs.mech) {
    print("The stone door grinds open, revealing a passage to an ancient crypt.");
    moveToRoom("crypt");
  } else if (d === "west" && room.sd === "crypt" && !gs.cry) {
    print("The wall here shows faint outlines of a hidden passage, but it remains sealed.");
  } else if (d === "west" && room.sd === "crypt" && gs.cry) {
    print("The hidden passage opens, revealing a path to an underground lake.");
    moveToRoom("underground lake");
  } else if (d === "down" && room.sd === "underground lake" && !gs.lake) {
    print("The lake is too deep and dark to enter safely.");
  } else if (d === "down" && room.sd === "underground lake" && gs.lake) {
    print("With the lake drained, you can now descend into the revealed chamber.");
    moveToRoom("lake chamber");
  } else if (room.e[d]) {
    moveToRoom(room.e[d]);
  } else {
    print("You cannot go that way");
  }
}

// Take item handler
export function handleTake(
  obj: string,
  char: Character,
  room: Room,
  handlers: CommandHandlers
): void {
  const { print } = handlers;

  if (obj === "all") {
    if (room.c && room.c.length > 0) {
      const items = room.c.slice();
      items.forEach(i => {
        if (i === "folding ladder") {
          print("The folding ladder is too heavy to carry, but you can move it between nearby rooms.");
        } else {
          print("You pick up the " + i);
          char.inv.push(i);
          removeFromArray(room.c, i);
        }
      });
      updateRoomDescriptions({}, {} as GameState); // Will be called properly from main
    } else {
      print("There is nothing to take!");
    }
  } else {
    const i = findMatch(room.c, obj);
    if (i) {
      if (i === "folding ladder") {
        print("The folding ladder is too heavy to carry, but you can move it between nearby rooms.");
      } else {
        print("You pick up the " + i);
        char.inv.push(i);
        removeFromArray(room.c, i);
      }
    } else {
      const objLower = obj.toLowerCase();
      if (cantTakeObjects[objLower]) {
        print(cantTakeObjects[objLower]);
      } else {
        print("There is no " + obj + " here.");
      }
    }
  }
}

// Drop item handler
export function handleDrop(
  obj: string,
  char: Character,
  room: Room,
  handlers: CommandHandlers
): void {
  const { print } = handlers;

  if (obj === "all") {
    if (char.inv && char.inv.length > 0) {
      while (char.inv.length) {
        const i = char.inv.pop()!;
        print("You dropped the " + i);
        room.c.push(i);
      }
    } else {
      print("You aren't carrying anything");
    }
  } else {
    const i = findMatch(char.inv, obj);
    if (i) {
      print("You drop the " + i + ".");
      room.c.push(i);
      removeFromArray(char.inv, i);
    } else {
      print("You aren't carrying a " + obj + ".");
    }
  }
}

// Examine handler
export function handleExamine(
  obj: string,
  char: Character,
  gs: GameState,
  dungeon: { [key: string]: Room },
  room: Room,
  handlers: CommandHandlers
): void {
  const { print, describe } = handlers;

  if (obj) {
    const i = findMatch(char.inv, obj) || findMatch(room.c, obj);
    if (i) {
      print("You examine the " + i + ". " + (itemDescs[i] || "You see nothing special about it."));
    } else {
      const objLower = obj.toLowerCase();
      const roomExamines = examineObjects[char.loc];

      if (roomExamines && roomExamines[objLower]) {
        const examineText = roomExamines[objLower];
        if (typeof examineText === 'function') {
          const roomsNeedingGameState = ["east room", "center room", "underground lake", "crypt",
            "basement", "puzzle room", "secret"];
          const roomsNeedingDungeon = ["attic room", "closet", "starting area"];

          if (roomsNeedingGameState.includes(char.loc)) {
            print((examineText as (gs: GameState) => string)(gs));
          } else if (roomsNeedingDungeon.includes(char.loc)) {
            print((examineText as (dungeon: { [key: string]: Room }) => string)(dungeon));
          } else {
            print((examineText as (gs: GameState) => string)(gs));
          }
        } else {
          print(examineText);
        }
      } else if (["room", "area", "here", "around", "surroundings"].includes(objLower)) {
        describe(room, true);
      } else {
        print("You don't see a " + obj + " here.");
      }
    }
  } else {
    describe(room, true);
  }
}

// Move item (ladder or puzzle blocks)
export function handleMoveItem(
  obj: string,
  char: Character,
  gs: GameState,
  dungeon: { [key: string]: Room },
  room: Room,
  runes: string[],
  handlers: CommandHandlers
): void {
  const { print, showPuzzle, moveToRoom } = handlers;

  if (obj.includes("ladder") && room.c.includes("folding ladder")) {
    if (char.loc === "closet") {
      print("You carry the ladder west to the attic room.");
      removeFromArray(room.c, "folding ladder");
      dungeon["attic room"].c.push("folding ladder");
      updateRoomDescriptions(dungeon, gs);
      moveToRoom("attic room");
    } else if (char.loc === "attic room") {
      print("You carry the ladder east to the closet.");
      removeFromArray(room.c, "folding ladder");
      dungeon["closet"].c.push("folding ladder");
      updateRoomDescriptions(dungeon, gs);
      moveToRoom("closet");
    } else {
      print("You can't move the ladder from here.");
    }
  } else if (char.loc === "puzzle room" && !gs.puzz) {
    const token = (obj || "").toLowerCase().trim().split(/\s+/).pop();
    if (!token) {
      print("Specify which rune block to move.");
      return;
    }

    let targetRune: number | null = null;
    for (let i = 1; i < runes.length; i++) {
      if (runes[i].trim().toLowerCase() === token) {
        targetRune = i;
        break;
      }
    }

    if (targetRune === null) {
      print("I don't know which rune you mean.");
      return;
    }

    const result = slideTile(gs.grid, targetRune);
    if (!result.success) {
      print("The " + token + " cannot move there. Blocks must be adjacent to the empty space.");
      return;
    }

    gs.grid = result.grid;
    print("You slide the " + token + " into the empty space.");

    if (isPuzzleSolved(gs.grid)) {
      print("The puzzle is complete! A hidden compartment opens, revealing a crystal orb!");
      room.c.push("crystal orb");
      gs.puzz = 1;
      updateRoomDescriptions(dungeon, gs);
    } else {
      showPuzzle();
    }
  } else {
    print("You can't move that.");
  }
}

// Use item handler - THE BIG ONE
export function handleUseItem(
  obj: string,
  char: Character,
  gs: GameState,
  dungeon: { [key: string]: Room },
  room: Room,
  runes: string[],
  handlers: CommandHandlers
): void {
  const { print, showPuzzle } = handlers;

  if (!obj) {
    print("Use what?");
    return;
  }

  const i = findMatch(char.inv, obj);
  if (!i) {
    print("You do not have a " + obj);
    return;
  }

  // Puzzle room sliding block logic
  if (char.loc === "puzzle room" && !gs.puzz) {
    const token = obj.toLowerCase().trim();
    let targetRune: number | null = null;
    for (let idx = 1; idx < runes.length; idx++) {
      if (runes[idx].trim().toLowerCase() === token) {
        targetRune = idx;
        break;
      }
    }
    if (targetRune !== null) {
      const result = slideTile(gs.grid, targetRune);
      if (!result.success) {
        print("The " + token + " cannot move there. Blocks can only slide into the empty space if adjacent.");
        return;
      }
      gs.grid = result.grid;
      print("You slide the " + token + " into the empty space.");
      if (isPuzzleSolved(gs.grid)) {
        print("The final block slides into place! The runes glow in sequence, and a hidden compartment opens. Inside, you find an ancient crystal orb!");
        room.c.push("crystal orb");
        gs.puzz = 1;
        updateRoomDescriptions(dungeon, gs);
      } else {
        showPuzzle();
      }
      return;
    }
  }

  // Magic scroll usage
  if (i === "magic scroll" && char.loc === "center room" && !gs.trap) {
    print("You hold up the magic scroll and the symbols glow brightly. The fungi above pulse in response, revealing a hidden trapdoor in the floor! The scroll dissolves into a puff of sweet-smelling smoke.");
    dungeon["center room"].e.down = "trapdoor hold";
    removeFromArray(char.inv, "magic scroll");
    gs.trap = 1;
    updateRoomDescriptions(dungeon, gs);
  }
  // Pail of water on brazier
  else if (i === "pail of water" && char.loc === "east room" && !gs.braz) {
    print("You douse the brazier. It's now dark, but star patterns are visible. Sifting through the ashes, you find a shimmering gem.");
    room.c.push("shiny gem");
    removeFromArray(char.inv, "pail of water");
    gs.braz = 1;
    updateRoomDescriptions(dungeon, gs);
  }
  // Crowbar on window
  else if (i === "crowbar" && char.loc === "attic room" && !dungeon["attic room"].e.south) {
    print("You swing the crowbar, smashing open the window!");
    dungeon["attic room"].e.south = "roof";
    updateRoomDescriptions(dungeon, gs);
  }
  // Crowbar on gargoyle (easter egg)
  else if (i === "crowbar" && char.loc === "roof") {
    gs.gcrow++;
    const a = gs.gcrow;
    if (a === 1) print("You attempt to hit the massive gargoyle with the crowbar. Your strike bounces harmlessly off its stone hide. The towering creature looks down at you with profound disinterest, as if a gnat had just bumped into its leg.");
    else if (a === 2) print("You swing the crowbar at the giant gargoyle again. This time the enormous beast lets out an exaggerated stone yawn that echoes like a rockslide, and taps one of its massive claws impatiently on the roof tiles, creating small cracks in the stone.");
    else if (a === 3) print("The colossal gargoyle watches your crowbar technique with obvious disappointment. It shakes its great head slowly and makes a 'tsk tsk' sound like grinding granite, the rumble vibrating through the roof beneath your feet.");
    else if (a === 4) print("The gargoyle holds up one claw in a 'wait' gesture, then demonstrates proper crowbar form with an imaginary tool. Its technique is flawless. It looks at you expectantly.");
    else if (a === 5) print("The gargoyle produces a small stone scorecard from nowhere and holds up a '3/10.' A faint sound of booing echoes from somewhere in the distance.");
    else if (a === 6) print("The gargoyle pulls out a stone notebook and quill, taking detailed notes about your 'Persistent Futile Crowbar Techniques.' It underlines something twice with obvious concern.");
    else if (a === 7) print("The giant gargoyle cups its massive claws around its mouth and calls out in ancient stone-speak. Three more enormous gargoyles materialize on nearby rooftops to watch your performance, their colossal forms creating imposing silhouettes against the night sky. They're all eating stone bags of popcorn.");
    else if (a === 8) print("The gathered gargoyles start placing bets with stone pebbles. Your gargoyle seems to be losing money on you. It gives you a pleading look that clearly says 'please stop embarrassing me in front of my friends.'");
    else if (a === 9) print("One of the spectator gargoyles sets up a stone concession stand. Another unfurls a banner reading 'CROWBAR FUTILITY EXHIBITION - ADMISSION FREE.' Your gargoyle buries its face in its claws.");
    else if (a === 10) print("The gargoyles have started livestreaming your attempts on 'GargoyleTube.' The chat is filled with laughing emoji carved in stone. Someone has donated 50 pebbles with the message 'STILL GOING?!'");
    else if (a === 11) print("A scholarly gargoyle in tiny spectacles arrives with a scroll titled 'A Dissertation on the Psychological Motivations Behind Repetitive Tool-Based Statue Assault.' It begins taking extensive notes while muttering about 'fascinating behavioral patterns.'");
    else if (a === 12) print("The gargoyles have erected a small museum around you. A bronze plaque reads: 'In this spot, an adventurer discovered the precise definition of insanity.' Tourists are taking pictures. Your gargoyle is charging admission.");
    else print("Your relentless crowbar attacks have torn a hole in the fabric of reality itself. Through the rift, you can see an infinite number of parallel universes where you're still trying to hit gargoyles with crowbars. The multiverse collectively sighs. At this point, the narrator must intervene: Please, for the love of all that is digital and holy, try literally ANYTHING else. The crowbar refuses to cooperate further until you attempt a different action.");
  }
  // Shiny gem to gargoyle
  else if (i === "shiny gem" && char.loc === "roof") {
    print("As you approach with the shimmering gem, the gargoyle's stone eyes widen with recognition. You hold out the gem and the creature carefully takes it in its talons, tucking it protectively under one wing. For a moment, nothing happens. Then the gargoyle opens its ancient maw wide, and with a wet *thunk*, drops a golden key onto the roof tiles. The key gleams but is covered in a thin layer of gargoyle saliva.");
    room.c.push("golden key");
    removeFromArray(char.inv, "shiny gem");
    gs.garg = 1;
    updateRoomDescriptions(dungeon, gs);
  }
  // Copper gear in basement
  else if (i === "copper gear" && char.loc === "basement" && !gs.gear) {
    print("You insert the copper gear. It fits perfectly and begins to glow!");
    removeFromArray(char.inv, "copper gear");
    gs.gear = 1;
    updateRoomDescriptions(dungeon, gs);
  }
  // Iron lever activation
  else if (i === "iron lever" && char.loc === "basement") {
    if (!gs.gear) {
      print("You try to pull the iron lever, but without the proper gear installed, nothing happens.");
    } else if (!gs.mech) {
      print("You pull the iron lever. Ancient machinery whirs to life, and a stone door grinds open to the west!");
      dungeon["basement"].e.west = "crypt";
      removeFromArray(char.inv, "iron lever");
      gs.mech = 1;

      // Remove all unused gears and levers from the game
      const unusedParts = ["brass gear", "wooden lever", "stone gear", "silver lever", "rusted gear"];
      let cleaned = false;

      unusedParts.forEach(part => {
        if (room.c.includes(part)) {
          removeFromArray(room.c, part);
          cleaned = true;
        }
        if (char.inv.includes(part)) {
          removeFromArray(char.inv, part);
          cleaned = true;
        }
      });

      if (cleaned) {
        print("Now that the mechanism is working, you discard the unused parts - they're no longer needed.");
      }

      updateRoomDescriptions(dungeon, gs);
    } else {
      print("The mechanism is already activated.");
    }
  }
  // Wrong gears
  else if (i === "brass gear" && char.loc === "basement") {
    print("You try to insert the brass gear, but it's the wrong size - too large for the mechanism.");
  }
  else if (i === "stone gear" && char.loc === "basement") {
    print("The stone gear is far too heavy and rough to fit properly in the delicate mechanism.");
  }
  else if (i === "rusted gear" && char.loc === "basement") {
    print("The rusted gear crumbles partly in your hands - too corroded to be functional.");
  }
  // Wrong levers
  else if (i === "wooden lever" && char.loc === "basement") {
    print("You try the wooden lever, but it's too brittle and flexible to operate the heavy machinery.");
  }
  else if (i === "silver lever" && char.loc === "basement") {
    print("The silver lever is beautifully crafted but too smooth - it slips and won't provide enough grip.");
  }
  // Crystal lens + mirror in crypt
  else if ((i === "crystal lens" || i === "ornate glass mirror") && char.loc === "crypt" &&
    char.inv.includes("crystal lens") && char.inv.includes("ornate glass mirror") && !gs.cry) {
    print("You focus light from the reflecting pool onto the celestial murals. The symbols blaze and a hidden passage opens west! Both items shatter from the intense magical light.");
    dungeon["crypt"].e.west = "underground lake";
    removeFromArray(char.inv, "ornate glass mirror");
    removeFromArray(char.inv, "crystal lens");
    gs.cry = 1;
    updateRoomDescriptions(dungeon, gs);
  }
  // Altar items - FIXED LOGIC
  else if ((i === "silver statue" || i === "spiral hourglass") && char.loc === "underground lake" &&
    char.inv.includes("silver statue") && char.inv.includes("spiral hourglass") && !gs.lake) {
    print("You reverently place both sacred objects into their matching indentations on the altar's surface. As the silver statue settles into the figurine-shaped recess and the spiral hourglass finds its place in the time-keeper's slot, ancient energies begin to stir.");
    print("");
    print("The altar blazes with brilliant light, and both items fuse permanently with the stone, glowing with ethereal radiance. The ground trembles as hidden mechanisms activate deep beneath the lake. With a tremendous rushing sound, the dark waters begin to drain away through ancient channels, spiraling down into unfathomable depths!");
    print("");
    print("As the waters recede, intricate patterns carved into the stone floor are revealed—patterns that have been hidden beneath the lake for millennia. Where once there was only dark water, stone steps now emerge, descending from the altar's base into a magnificent hidden chamber below.");
    dungeon["underground lake"].e.down = "lake chamber";
    removeFromArray(char.inv, "spiral hourglass");
    removeFromArray(char.inv, "silver statue");
    gs.lake = 1;
    updateRoomDescriptions(dungeon, gs);
  }
  // Quill pen uses - magic scroll
  else if (i === "quill pen" && char.loc === "library" && hasScrolls(char.inv) && !gs.mag) {
    gs.scrl--;
    print("You copy the glowing symbols from the tome onto a blank scroll. It becomes a magic scroll! (" + gs.scrl + " blank scrolls remain)");
    char.inv = removeScrolls(char.inv);
    char.inv.push("magic scroll");
    if (gs.scrl > 0) char.inv.push("pile of blank scrolls");
    gs.mag = 1;
  }
  // Complete star chart
  else if (i === "quill pen" && char.loc === "east room" && char.inv.includes("incomplete star chart") && gs.braz) {
    print("You complete the star chart using the patterns on the cooled brazier!");
    removeFromArray(char.inv, "incomplete star chart");
    char.inv.push("complete star chart");
  }
  // Use complete chart with telescope
  else if (i === "complete star chart" && char.loc === "observatory" && !gs.tel) {
    print("You align the telescope with the constellation. The chart glows and crumbles to stardust. A hidden compartment reveals a crystal lens!");
    room.c.push("crystal lens");
    removeFromArray(char.inv, "complete star chart");
    gs.tel = 1;
    updateRoomDescriptions(dungeon, gs);
  }
  // Small lantern in starting area - LANTERN CONSUMED AFTER USE
  else if (i === "small lantern" && char.loc === "starting area" && !dungeon["starting area"].e.west) {
    print("The lantern illuminates hidden magical symbols on the western wall! You press a crystalline button, and a passage opens to the west! The lantern flickers and dies, its magic exhausted.");
    dungeon["starting area"].e.west = "puzzle room";
    removeFromArray(char.inv, "small lantern");
    updateRoomDescriptions(dungeon, gs);
  }
  // Separate lantern from statue
  else if (i === "lantern bearing statue") {
    print("You carefully separate the lantern from the statue. You now have a silver statue and a small lantern.");
    removeFromArray(char.inv, "lantern bearing statue");
    char.inv.push("silver statue");
    char.inv.push("small lantern");
  }
  // Crystal orb in lake chamber
  else if (i === "crystal orb" && char.loc === "lake chamber" && !gs.orb) {
    print("The crystal orb blazes when you pass between the dice and card pillars! A silver emblem is revealed. The orb crumbles to stardust.");
    room.c.push("silver emblem");
    removeFromArray(char.inv, "crystal orb");
    gs.orb = 1;
    updateRoomDescriptions(dungeon, gs);
  }
  // Copy cipher
  else if (i === "quill pen" && char.loc === "lake chamber" && hasScrolls(char.inv) && !char.inv.includes("cipher scroll") && !char.inv.includes("deciphered scroll")) {
    gs.scrl--;
    print("You copy the mysterious cipher symbols onto a scroll. (" + gs.scrl + " blank scrolls remain)");
    char.inv = removeScrolls(char.inv);
    char.inv.push("cipher scroll");
    if (gs.scrl > 0) char.inv.push("pile of blank scrolls");
  }
  // Decipher in library
  else if (i === "cipher scroll" && char.loc === "library") {
    print("You decode the cipher: 'At the altar of games, speak the words of mystery: WHERE FOG FLOWS EAST, SECRETS AWAIT BELOW.'");
    removeFromArray(char.inv, "cipher scroll");
    char.inv.push("deciphered scroll");
  }
  // Speak at altar
  else if (i === "deciphered scroll" && char.loc === "underground lake" && !gs.ciph) {
    print("You speak the ancient words! The altar rumbles and reveals an ancient coin. The scroll crumbles to ash.");
    room.c.push("ancient coin");
    removeFromArray(char.inv, "deciphered scroll");
    gs.ciph = 1;
    updateRoomDescriptions(dungeon, gs);
  }
  // Dungeon map creation
  else if (i === "quill pen" && char.loc === "observatory" && hasScrolls(char.inv) && !char.inv.includes("dungeon map")) {
    gs.scrl--;
    print("You sketch out a dungeon map! (" + gs.scrl + " blank scrolls remain)");
    char.inv = removeScrolls(char.inv);
    char.inv.push("dungeon map");
    if (gs.scrl > 0) char.inv.push("pile of blank scrolls");
  }
  // Golden key on final chest - FIXED LOGIC
  else if ((i === "golden key" || i === "ancient coin" || i === "silver emblem") && char.loc === "secret") {
    const hc = char.inv.includes("ancient coin");
    const he = char.inv.includes("silver emblem");
    const hk = char.inv.includes("golden key");

    if (hc && he && hk && !gs.fin) {
      print("With reverent hands, you insert the ornate golden key into the traditional keyhole, feeling it slide home with a satisfying click. The ancient coin fits perfectly into the circular recess, its surface warming to your touch as mystical energies begin to stir. Finally, you place the silver emblem into the crested indentation, and the chest responds with a symphony of mechanical wonder.");
      print("");
      print("The three keyholes begin to glow with an otherworldly radiance—gold, silver, and copper light intertwining in mesmerizing patterns that dance across the chamber walls. Ancient mechanisms whir to life within the chest's depths, their clockwork harmonies echoing through the misty air like a celestial orchestra.");
      print("");
      print("The chest's lid trembles, then slowly—majestically—begins to rise. Tendrils of luminescent mist escape from within, carrying with them the scent of long-lost ages and forgotten magics. But instead of treasure, a blinding column of pure starlight erupts from the chest, shooting upward and tearing a rift in the very fabric of reality!");
      print("");
      print("The swirling portal pulses with colors that shouldn't exist, showing glimpses of impossible places—floating islands, inverted waterfalls, cities of crystal and shadow. A warm wind blows through from the other side, carrying whispers of adventure yet to come. This is your escape route... but to where? The portal hums with ancient power, waiting for you to step through into the unknown.");
      print("");
      print("🎉 CONGRATULATIONS! You've escaped the dungeon! 🎉");
      gs.fin = 1;
      updateRoomDescriptions(dungeon, gs);

      // Call completion handler if provided
      if (handlers.onGameComplete) {
        handlers.onGameComplete();
      }
    } else if (!hc || !he || !hk) {
      const missing = [];
      if (!hk) missing.push("golden key");
      if (!hc) missing.push("ancient coin");
      if (!he) missing.push("silver emblem");
      print("The chest requires three keys to open. Missing: " + missing.join(", "));
    } else {
      print("The escape portal still swirls before you, its otherworldly energies beckoning you to step through into the unknown realms beyond.");
    }
  }
  // Add this near the end of handleUseItem, before the final else statement:

    // Crowbar silly responses for wrong locations
    else if (i === "crowbar") {
      if (char.loc === "starting area") {
        print("You swing the crowbar at the barren rock walls. The crowbar bounces off with a disappointing *clang*. The walls remain unimpressed by your display of brute force.");
      } else if (char.loc === "center room") {
        print("You attempt to pry at the stone floor with the crowbar. After several minutes of fruitless effort, you realize you're essentially trying to crowbar a floor. A floor.");
      } else if (char.loc === "east room") {
        print("You wave the crowbar menacingly at the soaring columns. They continue to soar, utterly unmoved by your aggressive posturing with construction equipment.");
      } else if (char.loc === "library") {
        print("You raise the crowbar threateningly toward the ancient tomes. A dusty voice seems to whisper from the shelves: 'Violence is not the answer. Have you tried reading?'");
      } else if (char.loc === "observatory") {
        print("You consider using the crowbar on the delicate telescope mechanisms. Even you can tell this is a spectacularly terrible idea. The telescope seems to tilt away from you judgmentally.");
      } else if (char.loc === "basement") {
        print("You tap the crowbar against the ancient mechanisms. They respond with a series of grinding clicks that somehow sound deeply offended.");
      } else if (char.loc === "trapdoor hold") {
        print("You jab the crowbar into the earthen walls. Dirt falls on your head. You have achieved: dirt in hair. Congratulations.");
      } else if (char.loc === "crypt") {
        print("You wave the crowbar at the sarcophagi. The ancient dead do not respond to your property damage threats. They've seen worse.");
      } else if (char.loc === "underground lake") {
        print("You attempt to crowbar the altar. It's solid stone and weighs approximately one metric ton. The crowbar bounces off. Physics wins again.");
      } else if (char.loc === "lake chamber") {
        print("You experimentally poke the gaming pillars with the crowbar. They're carved from solid stone and completely unimpressed by your attempts at architectural vandalism.");
      } else if (char.loc === "closet") {
        print("You swing the crowbar at the musty walls. Cobwebs fall on you. The fog swirls mockingly. You've accomplished nothing except becoming dustier.");
      } else if (char.loc === "secret") {
        print("You consider crowbarring the ornate chest open. But that would defeat the entire purpose of collecting three keys, wouldn't it? The chest seems to radiate an aura of 'nice try, buddy.'");
      } else if (char.loc === "puzzle room") {
        print("You attempt to use the crowbar to forcibly rearrange the puzzle blocks. They're each approximately 200 pounds of carved stone. Your arms give out after moving one half an inch. Perhaps there's a more elegant solution.");
      } else {
        print("You swing the crowbar around experimentally. Nothing happens. Maybe the crowbar isn't the solution to every problem.");
      }
    }

  else {
    print('The ' + i + ' does nothing here.');
  }
}